const WEBHOOK_EXCEPTION_MESSAGE = 'http exception when calling webhook';

const CONTROL_API_TRANSIENT_STATUS = /control api status:\s*(502|503|504)\b/;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const isRetryableMessage = message =>
  typeof message === 'string' &&
  (message.includes(WEBHOOK_EXCEPTION_MESSAGE) || CONTROL_API_TRANSIENT_STATUS.test(message));

function isThrownRetryable(error) {
  if (!error) return false;
  if (isRetryableMessage(error.message)) return true;
  if (error.graphQLErrors?.some(graphQLError => isRetryableMessage(graphQLError?.message))) return true;
  if (error.networkError?.result?.errors?.some(resultError => isRetryableMessage(resultError?.message))) return true;
  return false;
}

function responseHasRetryableError(response) {
  const { errors } = response ?? {};
  if (!errors) return false;
  if (Array.isArray(errors)) return errors.some(responseError => isRetryableMessage(responseError?.message));
  return isRetryableMessage(errors.message);
}

// Full-jitter exponential backoff: random(0, min(maxDelayMs, baseDelayMs * factor^n)).
// The jitter desynchronizes the concurrent bulk-deploy workers so a wave of failures
// doesn't retry in lockstep and re-saturate the control plane.
function computeBackoffDelay(attempt, { baseDelayMs, maxDelayMs, factor }) {
  const ceiling = Math.min(maxDelayMs, baseDelayMs * factor ** (attempt - 1));
  return Math.random() * ceiling;
}

export default async function withTransientRetry(
  operation,
  { attempts = 5, baseDelayMs = 500, maxDelayMs = 8000, factor = 2 } = {}
) {
  let lastResult;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const isLastAttempt = attempt === attempts;

    try {
      lastResult = await operation();
      if (isLastAttempt || !responseHasRetryableError(lastResult)) {
        return lastResult;
      }
    } catch (error) {
      if (isLastAttempt || !isThrownRetryable(error)) throw error;
    }

    await sleep(computeBackoffDelay(attempt, { baseDelayMs, maxDelayMs, factor }));
  }

  return lastResult;
}
